import React, { useState } from "react";

import toast from "react-hot-toast";
import { CreditCard, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";

import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { usePmHandover } from "@/hooks/use-pm-handover";
import { useEmbedStore } from "@/stores/embed-store";
import { billingApi } from "@/lib/api/billing";

/**
 * App-wide payment-method handover bar (shell attention slot).
 *
 * Slim violet bar mounted once in DashboardLayoutFull, under the PageBar.
 * Follows the NEW workspace owner to every dashboard route while
 * `pendingPmHandover` is true. Resolves only by decision — "Add my card"
 * (Stripe Checkout) or "Keep current card" (dismiss endpoint). No dismiss X.
 * Shares state with the billing-page banner via the use-pm-handover store.
 */
export function PmHandoverBar(): JSX.Element | null {
  const [addCardLoading, setAddCardLoading] = useState(false);
  const [dismissLoading, setDismissLoading] = useState(false);

  const { getIdToken } = useAuth();
  const { isOwner } = usePermissions();
  const isEmbedded = useEmbedStore((s) => s.isEmbedded);
  const { pending, refresh, resolveLocally } = usePmHandover();

  if (!pending || !isOwner || isEmbedded) {
    return null;
  }

  const handleAddCard = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    event.preventDefault();
    try {
      setAddCardLoading(true);
      const token = await getIdToken();
      if (!token) {
        toast.error("Authentication required");
        return;
      }
      const { url } = await billingApi.addCard(token);
      window.location.href = url;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(
        err.response?.data?.message || "Failed to start add-card flow",
      );
      setAddCardLoading(false);
    }
  };

  const handleKeepCurrentCard = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    event.preventDefault();
    try {
      setDismissLoading(true);
      const token = await getIdToken();
      if (!token) {
        toast.error("Authentication required");
        return;
      }
      await billingApi.dismissPmHandover(token);
      toast.success("Keeping the current card on file");
      resolveLocally();
      void refresh(true);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || "Failed to dismiss");
    } finally {
      setDismissLoading(false);
    }
  };

  return (
    <div
      data-testid="pm-handover-bar"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3"
    >
      <CreditCard className="h-4 w-4 flex-shrink-0 text-primary" />
      <span className="min-w-0 text-sm text-foreground">
        <strong className="font-semibold">You now own this workspace.</strong>{" "}
        <span className="text-muted-foreground">
          Billing still charges the previous owner's card.
        </span>
      </span>
      <div className="ml-auto flex flex-shrink-0 items-center gap-2">
        <Button
          size="sm"
          onClick={handleAddCard}
          disabled={addCardLoading || dismissLoading}
          className="h-7 border border-transparent text-xs"
        >
          {addCardLoading && (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          )}
          Add my card
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={handleKeepCurrentCard}
          disabled={addCardLoading || dismissLoading}
          className="h-7 text-xs"
        >
          {dismissLoading && (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          )}
          Keep current card
        </Button>
      </div>
    </div>
  );
}
